import { css, html, nothing } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { uploadFile } from '../../lib/upload.js';

/**
 * <upload-progress> — handles file upload with progress display.
 *
 * Usage:
 *   <upload-progress @upload-complete="${(e) => handleUpload(e.detail)}"></upload-progress>
 *
 * The component shows a drop zone. When a file is dropped or selected, it uploads
 * via the presigned upload flow, showing progress. On completion it fires
 * `upload-complete` with `{ upload_id, status, file }` in the detail.
 *
 * Attributes:
 *   accept — file input accept string (default: "image/png,image/jpeg,image/webp,video/webm")
 *   label  — drop zone label (default: "Drop file here or click to browse")
 */
class UploadProgress extends BaseComponent {
    static properties = {
        accept: { type: String },
        label: { type: String },
        // Optional API client override; uploads use the assets API client
        // when it isn't set.
        api: { type: Object, attribute: false },
        _file: { type: Object, state: true },
        _preview: { type: String, state: true },
        _phase: { type: String, state: true }, // idle, hashing, uploading, processing, complete, error
        _progress: { type: Number, state: true },
        _error: { type: String, state: true },
        _rejectionMessage: { type: String, state: true },
        _dragOver: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
        .drop-zone {
            border: 2px dashed #ccc; border-radius: 8px; padding: 1.5rem; text-align: center;
            cursor: pointer; transition: border-color 0.15s, background-color 0.15s; position: relative;
        }
        .drop-zone:hover, .drop-zone.drag-over { border-color: #0d6efd; background-color: #f0f6ff; }
        .drop-zone input[type="file"] { position: absolute; width: 0; height: 0; opacity: 0; }
        .preview { max-width: 100%; max-height: 160px; object-fit: contain; border-radius: 4px; margin-bottom: 0.5rem; }
        .progress-bar-container {
            width: 100%; height: 6px; background: #e9ecef; border-radius: 3px; overflow: hidden; margin: 0.75rem 0;
        }
        .progress-bar-fill {
            height: 100%; background: #0d6efd; border-radius: 3px; transition: width 0.15s;
        }
        .progress-bar-fill.processing { background: #6c757d; animation: pulse 1.5s ease-in-out infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .file-info {
            display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-top: 0.25rem;
        }
        .phase-label { font-size: 0.8rem; color: #666; }
    `;

    constructor() {
        super();
        this.accept = 'image/png,image/jpeg,image/webp,video/webm';
        this.label = 'Drop file here or click to browse';
        /** @type {{post: Function, get: Function, baseUrl?: string} | null} */
        this.api = null;
        this._file = null;
        this._preview = null;
        this._phase = 'idle';
        this._progress = 0;
        this._error = null;
        this._rejectionMessage = null;
        this._dragOver = false;
    }

    reset() {
        if (this._preview) URL.revokeObjectURL(this._preview);
        this._file = null;
        this._preview = null;
        this._phase = 'idle';
        this._progress = 0;
        this._error = null;
        this._rejectionMessage = null;
    }

    /**
     * Returns true if `file.type` matches the comma-separated `accept`
     * attribute. Each accept entry is either an exact MIME type
     * ("image/png") or a wildcard top-level type ("image/*"). Files with
     * an empty `type` or the generic `application/octet-stream` (some
     * browsers can't sniff the MIME from extension — most notably PSD)
     * are accepted — the server will validate.
     * @param {File} file
     * @returns {boolean}
     */
    _isAcceptedFile(file) {
        if (!file.type || file.type === 'application/octet-stream') return true;
        const entries = (this.accept || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (entries.length === 0) return true;
        return entries.some((entry) => {
            if (entry.startsWith('.')) {
                // Extension entry (e.g. ".psd", ".svg") — match the file name, not
                // the MIME. A PSD reports type "image/vnd.adobe.photoshop", which
                // no MIME entry here would match.
                return file.name.toLowerCase().endsWith(entry.toLowerCase());
            }
            if (entry.endsWith('/*')) {
                const top = entry.slice(0, -1); // "image/"
                return file.type.startsWith(top);
            }
            return file.type === entry;
        });
    }

    /**
     * Friendly label for an unsupported file. Uses the file extension when
     * present, otherwise the raw MIME type.
     * @param {File} file
     * @returns {string}
     */
    _unsupportedFileLabel(file) {
        const dot = file.name.lastIndexOf('.');
        if (dot > 0 && dot < file.name.length - 1) {
            return file.name.slice(dot + 1).toUpperCase();
        }
        return file.type || 'unknown';
    }

    /**
     * Show an inline rejection message under the idle drop zone without
     * entering the "uploaded file with delete button" UI — the file was
     * never accepted, so there's nothing to delete or upload.
     * @param {File} file
     */
    _rejectUnsupported(file) {
        this._rejectionMessage = `${this._unsupportedFileLabel(file)} files aren't supported here. Drop an image or video that matches: ${this.accept}.`;
    }

    /**
     * Public API: Reset the component to idle and surface a rejection
     * message inline. Use this from a parent that does post-upload
     * validation (e.g., a submission endpoint that rejects on dimensions
     * or content rules) so the user sees the error in the same place
     * they dropped the file, and the now-orphan preview/delete row
     * disappear.
     * @param {string} message
     */
    showRejection(message) {
        if (this._preview) URL.revokeObjectURL(this._preview);
        this._file = null;
        this._preview = null;
        this._phase = 'idle';
        this._progress = 0;
        this._error = null;
        this._rejectionMessage = message;
    }

    _handleDrop(e) {
        e.preventDefault();
        this._dragOver = false;
        const file = e.dataTransfer.files[0];
        if (!file) return;
        this._rejectionMessage = null;
        if (!this._isAcceptedFile(file)) {
            this._rejectUnsupported(file);
            return;
        }
        this._startUpload(file);
    }

    _handleFileInput(e) {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        this._rejectionMessage = null;
        if (!this._isAcceptedFile(file)) {
            this._rejectUnsupported(file);
            return;
        }
        this._startUpload(file);
    }

    async _startUpload(file) {
        if (this._preview) URL.revokeObjectURL(this._preview);
        this._file = file;
        this._preview = URL.createObjectURL(file);
        this._error = null;
        this._phase = 'hashing';
        this._progress = 0;

        try {
            const result = await uploadFile(file, {
                api: this.api ?? null,
                onProgress: (p) => {
                    this._phase = 'uploading';
                    this._progress = p;
                },
                onPhase: (phase) => {
                    this._phase = phase;
                },
            });

            this._phase = 'complete';
            this._progress = 1;
            this.dispatchEvent(
                new CustomEvent('upload-complete', {
                    bubbles: true,
                    composed: true,
                    detail: result,
                }),
            );
        } catch (err) {
            this._phase = 'error';
            this._error = err.message;
        }
    }

    _renderPreview() {
        if (!this._preview) return nothing;
        if (this._file?.type === 'video/webm') {
            return html`<video class="preview" src="${this._preview}" autoplay loop muted playsinline></video>`;
        }
        return html`<img class="preview" src="${this._preview}" alt="Preview">`;
    }

    _phaseLabel() {
        switch (this._phase) {
            case 'hashing':
                return 'Computing file hash...';
            case 'uploading':
                return `Uploading... ${Math.round(this._progress * 100)}%`;
            case 'processing':
                return 'Processing...';
            case 'complete':
                return 'Complete';
            case 'error':
                return this._error;
            default:
                return '';
        }
    }

    render() {
        if (this._phase !== 'idle') {
            return html`
                ${this._renderPreview()}
                <div class="file-info">
                    <span class="text-muted" style="font-size: 0.85rem;">${this._file?.name}</span>
                    ${
                        this._phase !== 'complete' && this._phase !== 'error'
                            ? nothing
                            : html`
                        <button type="button" class="btn btn-link btn-sm p-0 text-danger" style="font-size: 0.85rem; line-height: 1;"
                            @click="${() => this.reset()}">
                            <i class="bi bi-trash"></i>
                        </button>
                    `
                    }
                </div>
                ${
                    this._phase === 'error'
                        ? html`
                    <div class="text-danger small mt-1">${this._error}</div>
                `
                        : html`
                    <div class="progress-bar-container">
                        <div class="progress-bar-fill ${this._phase === 'processing' ? 'processing' : ''}"
                             style="width: ${this._phase === 'processing' ? '100' : Math.round(this._progress * 100)}%"></div>
                    </div>
                    <div class="phase-label">${this._phaseLabel()}</div>
                `
                }
            `;
        }

        return html`
            <div class="drop-zone ${this._dragOver ? 'drag-over' : ''}"
                 @drop="${this._handleDrop}"
                 @dragover="${(e) => {
                     e.preventDefault();
                     this._dragOver = true;
                 }}"
                 @dragleave="${() => (this._dragOver = false)}"
                 @click="${() => /** @type {HTMLElement} */ (this.shadowRoot.querySelector('#file-input')).click()}">
                <input type="file" id="file-input" accept="${this.accept}" @change="${this._handleFileInput}">
                <i class="bi bi-cloud-arrow-up" style="font-size: 1.5rem; color: #999;"></i>
                <p class="text-muted small mb-0 mt-1">${this.label}</p>
            </div>
            ${
                this._rejectionMessage
                    ? html`
                <div class="text-danger small mt-1">${this._rejectionMessage}</div>
            `
                    : nothing
            }
        `;
    }
}

customElements.define('upload-progress', UploadProgress);
